import { useMutation } from '@tanstack/react-query';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type {
  CreatePaymentAPInvoiceInput,
  CreatePaymentAPInvoiceResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

export interface UseCreatePaymentForAPInvoiceOptions {
  onSuccess?: (
    data: CreatePaymentAPInvoiceResponse,
    variables: CreatePaymentAPInvoiceInput,
  ) => void;
}

export function useCreatePaymentForAPInvoice(
  options?: UseCreatePaymentForAPInvoiceOptions,
) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn(
    (orpc, { id }: { id: string }) => [
      orpc.accountsPayable.invoices.get.queryKey({ input: { id } }),
      orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
    ],
  );
  const { showToast } = useToast();

  return useMutation(
    orpc.accountsPayable.invoices.createPayment.mutationOptions({
      onSuccess: (data, variables) => {
        showToast({
          title: 'Success',
          message: 'Payment created successfully',
          color: 'success',
          icon: 'check',
        });

        invalidateRpcQueryFn({ id: variables.id });

        options?.onSuccess?.(data, variables);
      },
      onError: (error) => {
        showToast({
          title: 'Error',
          message: error.message || 'Failed to create payment',
          color: 'error',
          icon: 'x',
        });
      },
    }),
  );
}
